import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PERMISSION_LABELS, PERMISSION_GROUPS, useMe } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger, AlertDialogDescription } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, ShieldCheck, User as UserIcon, MessageCircle } from "lucide-react";

const emptyForm = { name: "", email: "", password: "", permissions: [] };
const brandName = () => { try { return JSON.parse(localStorage.getItem("user") || "null")?.name || "a equipe"; } catch { return "a equipe"; } };

function shareCredentials(u, password) {
  const loginUrl = `${window.location.origin}/login`;
  const msg =
    `Olá, ${u.name}! Você foi cadastrado(a) na agenda online.\n\n` +
    `🔗 Acesso: ${loginUrl}\n` +
    `📧 Email: ${u.email}\n` +
    `🔑 Senha: ${password}\n\n` +
    `Por favor, altere sua senha após o primeiro acesso.`;
  window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
}

export default function Users() {
  const me = useMe();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => setList((await api.get("/users")).data);
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ name: u.name, email: u.email, password: "", permissions: u.permissions || [] }); setOpen(true); };

  const togglePerm = (p) => setForm((f) => ({
    ...f, permissions: f.permissions.includes(p) ? f.permissions.filter((x) => x !== p) : [...f.permissions, p]
  }));

  const save = async () => {
    try {
      if (editing) {
        const payload = { name: form.name, email: form.email, permissions: form.permissions };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("Usuário salvo");
      } else {
        const { data } = await api.post("/users", form);
        toast.success("Usuário criado");
        // Offer to share credentials
        if (window.confirm(`Enviar credenciais de ${data.name} via WhatsApp?`)) {
          shareCredentials(data, form.password);
        }
      }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao salvar"); }
  };
  const remove = async (id) => {
    try { await api.delete(`/users/${id}`); toast.success("Removido"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Falha"); }
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">Usuários</h1>
          <p className="text-sm text-muted-foreground mt-1">Cadastre usuários da sua equipe e defina o que cada um pode fazer.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="btn-new-user" onClick={openNew} className="h-11"><Plus className="h-4 w-4 mr-2" /> Novo usuário</Button></DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? "Editar usuário" : "Novo usuário"}</DialogTitle></DialogHeader>
            <div className="grid gap-4">
              <div className="space-y-2"><Label>Nome</Label><Input data-testid="input-user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Email</Label><Input data-testid="input-user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div className="space-y-2"><Label>{editing ? "Nova senha (opcional)" : "Senha"}</Label><Input data-testid="input-user-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editing ? "Deixe em branco para manter" : ""} /></div>
              </div>

              <div className="surface-muted p-4 space-y-3">
                <div className="text-sm font-medium flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Permissões</div>
                {editing?.role === "owner" ? (
                  <div className="text-xs text-muted-foreground">O proprietário tem todas as permissões automaticamente.</div>
                ) : (
                  PERMISSION_GROUPS.map((g) => (
                    <div key={g.name}>
                      <div className="text-xs uppercase tracking-widest text-muted-foreground mt-2 mb-1">{g.name}</div>
                      <div className="space-y-2">
                        {g.perms.map((p) => (
                          <label key={p} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`perm-${p}`}>
                            <Checkbox checked={form.permissions.includes(p)} onCheckedChange={() => togglePerm(p)} />
                            <span>{PERMISSION_LABELS[p]}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            <DialogFooter><Button data-testid="btn-save-user" onClick={save}>Salvar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="surface divide-y divide-border">
        {list.map((u) => (
          <div key={u.id} data-testid={`user-row-${u.id}`} className="p-4 flex items-center gap-4 hover:bg-muted/40 transition-colors">
            <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center"><UserIcon className="h-5 w-5 text-muted-foreground" /></div>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{u.name} {me?.id === u.id && <span className="text-xs text-muted-foreground">(você)</span>}</div>
              <div className="text-xs text-muted-foreground truncate">{u.email}</div>
              {u.role !== "owner" && (
                <div className="text-xs text-muted-foreground mt-1">{u.permissions.length} permissões</div>
              )}
            </div>
            <span className={`text-xs px-2 py-1 rounded border ${u.role === "owner" ? "border-accent/40 text-accent-foreground bg-accent/10" : "border-border text-muted-foreground"}`}>
              {u.role === "owner" ? "Proprietário" : "Staff"}
            </span>
            <Button data-testid={`btn-edit-user-${u.id}`} variant="ghost" size="sm" onClick={() => openEdit(u)} disabled={u.role === "owner" && u.id !== me?.id}><Pencil className="h-4 w-4" /></Button>
            {u.role !== "owner" && u.id !== me?.id && (
              <AlertDialog>
                <AlertDialogTrigger asChild><Button data-testid={`btn-del-user-${u.id}`} variant="ghost" size="sm" className="text-destructive hover:text-destructive"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Excluir "{u.name}"?</AlertDialogTitle><AlertDialogDescription>Essa ação não pode ser desfeita.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => remove(u.id)}>Excluir</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
